import React, { useCallback, useState } from 'react'
import { Box, Typography, Paper, IconButton } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

/**
 * Componente para cargar archivos GeoJSON mediante drag & drop
 */
const FileUploader = ({ onFileLoad, currentValue }) => {
    const [isDragging, setIsDragging] = useState(false)
    const [fileName, setFileName] = useState(null)

    const processFile = useCallback((file) => {
        const validExtensions = ['.geojson', '.json']
        const extension = '.' + file.name.split('.').pop().toLowerCase()

        if (!validExtensions.includes(extension)) {
            alert('Por favor selecciona un archivo .geojson o .json')
            return
        }

        const reader = new FileReader()
        reader.onload = (event) => {
            try {
                const content = event.target.result
                setFileName(file.name)
                onFileLoad(content)
            } catch (err) {
                alert('Error al leer el archivo: ' + err.message)
            }
        }
        reader.readAsText(file)
    }, [onFileLoad])

    const handleDragOver = useCallback((e) => {
        e.preventDefault()
        setIsDragging(true)
    }, [])

    const handleDragLeave = useCallback((e) => {
        e.preventDefault()
        setIsDragging(false)
    }, [])

    const handleDrop = useCallback((e) => {
        e.preventDefault()
        setIsDragging(false)

        const files = e.dataTransfer?.files
        if (files && files.length > 0) {
            processFile(files[0])
        }
    }, [processFile])

    const handleFileSelect = useCallback((e) => {
        const files = e.target.files
        if (files && files.length > 0) {
            processFile(files[0])
        }
    }, [processFile])

    const handleClear = () => {
        setFileName(null)
        onFileLoad('')
    }

    const hasValue = Boolean(currentValue)

    return (
        <Box>
            {!hasValue ? (
                <Paper
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                        border: '2px dashed',
                        borderColor: isDragging ? 'primary.main' : 'grey.400',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: isDragging ? 'action.hover' : 'background.default',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'action.hover',
                        },
                    }}
                    component="label"
                >
                    <input
                        type="file"
                        accept=".geojson,.json"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    <CloudUploadIcon
                        sx={{
                            fontSize: 48,
                            color: isDragging ? 'primary.main' : 'grey.500',
                            mb: 2,
                        }}
                    />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Arrastra un archivo aquí
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                        o haz clic para seleccionar
                    </Typography>
                    <Typography variant="caption" display="block" color="text.disabled" sx={{ mt: 1 }}>
                        Formatos aceptados: .geojson, .json
                    </Typography>
                </Paper>
            ) : (
                <Paper
                    sx={{
                        border: '2px solid',
                        borderColor: 'success.main',
                        borderRadius: 2,
                        p: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                            Archivo cargado
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InsertDriveFileIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                {fileName || 'geojson-data.json'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={handleClear}
                        color="error"
                        title="Eliminar archivo"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Paper>
            )}
        </Box>
    )
}

export default FileUploader
